/**
 * TrackList Component
 */

import { addTracksToQueue } from '../../../../redux/actions/player';
import { getPlayerTracksFromResults } from '../../../../redux/helpers/player';
import { environment } from '../../../../relay';
import { trackSearchFragment, trackSearchQuery } from '../../../../relay/trackSearch';
import { RelayOperationBase } from '../../../../utils/relay';
import './styles.scss';

import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createFragmentContainer, QueryRenderer } from 'react-relay';

import { pullAllWith } from 'lodash';
import { QueryStringContext } from '../../../../contexts/queryString';
import { ActionSource } from '../../../../redux/actions/analytics';
import LoadingSpinner from '../../../atoms/feedback/LoadingSpinner';
import Heading from '../../../atoms/text/Heading';
import { CardOwnProps } from '../../../molecules/media/Card';
import { SignUpCtaCard } from '../../../molecules/media/SignUpCtaCard';
import TrackListItem from '../../../molecules/search/TrackListItem';

interface IOwnInheritedProps {
  actionSource: ActionSource;
  showSignUpCta?: boolean;
  cardProps?: Partial<CardOwnProps>;
  className?: string;
  displayPriceElement?: boolean;
  excludeSearchResults?: string[];
  showAllButton?: JSX.Element;
  title?: JSX.Element | string;
  shouldAddTracksToQueue?: boolean;
  onResult?(results: GQL.ITrackSearchResult[]): void;
}

interface IOwnDispatchProps {
  addTracksToPlayer: () => void;
}

interface IOwnProps extends IOwnInheritedProps {
  tracks?: GQL.ITrackSearchResult[];
  viewer?: GQL.IViewer;
  showListenedStatus?: boolean;
}

interface IOwnStateProps {
  isUserLoggedIn: boolean;
}

type TrackListProps = IOwnProps & IOwnStateProps & IOwnDispatchProps;

interface IQueryRendererProps extends IOwnInheritedProps {
  searchQuery: ITrackSearchQueryVariables;
}

const classes = new BEM('track-list');

function getTrackResultsFromEdges(viewer: GQL.IViewer) {
  if (viewer.trackSearch && viewer.trackSearch.edges) {
    return viewer.trackSearch!.edges!.map(({ node }) => node!);
  }
  return [];
}

const TrackListHeader = ({ isLoading, title }: { isLoading?: boolean; title: JSX.Element | string; }) => {
  return typeof title === 'string' ?
    <Heading is="h3" textColor={isLoading ? 'bw6' : undefined} {...classes('title') }>{title}</Heading>
    : title;
};

class TrackListInternal extends React.Component<TrackListProps, IOwnStateProps> {
  static defaultProps: Partial<IOwnProps> = {
    showListenedStatus: true,
  };

  results: GQL.ITrackSearchResult[];

  componentDidMount() {
    const {
      addTracksToPlayer,
      onResult,
      shouldAddTracksToQueue,
    } = this.props;

    if (shouldAddTracksToQueue || shouldAddTracksToQueue === undefined) {
      addTracksToPlayer();
    }

    if (onResult) {
      onResult(this.getResultset());
    }
  }

  render() {
    const {
      actionSource,
      cardProps,
      className,
      displayPriceElement,
      showAllButton,
      showSignUpCta,
      title,
      viewer,
    } = this.props;

    const results = this.getResultset();

    return results && results.length ? (
      <div {...classes('', '', className) }>
        {title && <TrackListHeader title={title} />}
        <ul>
          {results.reduce(
            (list, track, i) => {
              list.push(
                <TrackListItem
                  actionSource={actionSource}
                  key={track.id}
                  track={track}
                  cardProps={cardProps}
                  displayPriceElement={displayPriceElement}
                />,
              );
              if (showSignUpCta && !this.props.isUserLoggedIn && (i === 1 || results.length <= 1)) {
                list.push(
                  <li {...classes('item', '', 'sign-up-card') } key="sign-up-card">
                    <SignUpCtaCard viewer={viewer} />
                  </li>,
                );
              }
              return list;
            }, [] as any,
          )}
        </ul>
        {viewer && results.length < viewer.trackSearch!.total! && showAllButton}
      </div>
    ) : null;
  }

  getResultset() {
    const {
      excludeSearchResults,
      tracks,
      viewer,
    } = this.props;

    const results = !!viewer && !!viewer.trackSearch ?
      viewer.trackSearch!.edges!.map(({ node }) => node!)
      : tracks || [];

    if (excludeSearchResults && excludeSearchResults.length) {
      pullAllWith(results, excludeSearchResults, (a, id) => a.id === id);
    }

    return results;
  }
}

const ConnectedTrackList = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps, IStoreState>(
  ({ user }, props) => ({
    ...props,
    isUserLoggedIn: user.isLoggedIn,
  }),
  (dispatch, { tracks, viewer }) => ({
    addTracksToPlayer: () => {
      const dataset = viewer ? getTrackResultsFromEdges(viewer) : tracks || [];
      const playerTracks = getPlayerTracksFromResults(dataset);
      dispatch(addTracksToQueue({ tracks: playerTracks }));
    },
  }),
)(TrackListInternal);

export default ConnectedTrackList;

const TrackListRelay = createFragmentContainer(
  ConnectedTrackList,
  trackSearchFragment,
);

type TrackListQueryOperation = RelayOperationBase<ITrackSearchQueryVariables, TrackListProps>;

export class TrackListQuery extends React.Component<IQueryRendererProps> {
  isComponentMounted = false;

  componentDidMount() {
    this.isComponentMounted = true;
  }

  shouldComponentUpdate() {
    return this.isComponentMounted;
  }

  componentDidUpdate() {
    this.isComponentMounted = true;
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
  }

  getQueryRenderer(licenseParams?: GQL.ILicenseUsageParamsInput[]) {
    const { searchQuery, ...componentProps } = this.props;

    return (
      <QueryRenderer<TrackListQueryOperation>
        environment={environment}
        query={trackSearchQuery}
        variables={{ license_params: licenseParams, ...searchQuery }}
        render={({ error, props }) => {
          if (error) {
            return <div>{error.message}</div>;
          } else if (props) {
            return <TrackListRelay {...props} {...componentProps} />;
          }
          return (
            <div {...classes('', 'loading', componentProps.className)}>
              {componentProps.title && <TrackListHeader isLoading={true} title={componentProps.title} />}
              <LoadingSpinner size="small" {...classes('loading-spinner')} />
            </div>
          );
        }}
      />
    );
  }

  render() {
    return this.props.searchQuery.license_params ?
      this.getQueryRenderer()
      : (
        <QueryStringContext.Consumer>
          {({ license_params }) => this.getQueryRenderer(license_params)}
        </QueryStringContext.Consumer>
      );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/search/TrackList/index.tsx